/**
 * Page-scope layouts: slot fractions are of ONE page. `spread.applyLayout` can apply them to the
 * left page, the right page, or both (`page: 'both'` mirrors the layout onto each page and suffixes
 * the slot ids with `-left` / `-right`).
 */
import type { Layout } from '@freealbum/document';
import {
  ALL_FAMILIES,
  FULL_BOX,
  GUTTER_FRAC,
  TALL_FAMILIES,
  WIDE_FAMILIES,
  band,
  grid,
  innerBox,
  layout,
  slot,
  sub,
} from './_helpers';

const box = innerBox('page');
const G = GUTTER_FRAC;

export const PAGE_LAYOUTS: Layout[] = [
  // ---------- 1 photo ----------
  layout({
    id: 'pg-1-full-bleed',
    name: 'Full-bleed page',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['full-bleed', 'hero', 'single'],
    marginFrac: 0,
    slots: [slot({ id: 'a', ...FULL_BOX })],
  }),
  layout({
    id: 'pg-1-framed',
    name: 'One framed photo',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['single', 'calm'],
    slots: [slot({ id: 'a', ...box })],
  }),
  layout({
    id: 'pg-1-portrait',
    name: 'Standing portrait',
    scope: 'page',
    families: TALL_FAMILIES,
    tags: ['single', 'portrait', 'for-portrait'],
    slots: [slot({ id: 'a', ...sub(box, 0.14, 0, 0.72, 1) })],
  }),
  layout({
    id: 'pg-1-landscape',
    name: 'Wide photo, centred',
    scope: 'page',
    families: WIDE_FAMILIES,
    tags: ['single', 'landscape', 'for-landscape'],
    slots: [slot({ id: 'a', ...sub(box, 0, 0.2, 1, 0.6) })],
  }),
  layout({
    id: 'pg-1-caption',
    name: 'Photo with a caption',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['single', 'caption', 'journal'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.84) }),
      slot({ id: 'c', kind: 'text', role: 'caption', ...sub(box, 0, 0.88, 1, 0.12) }),
    ],
  }),
  layout({
    id: 'pg-1-title-caption',
    name: 'Title, photo, caption',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['single', 'title', 'caption', 'journal'],
    slots: [
      slot({ id: 't', kind: 'text', role: 'title', ...sub(box, 0, 0, 1, 0.12) }),
      slot({ id: 'a', ...sub(box, 0, 0.16, 1, 0.7) }),
      slot({ id: 'c', kind: 'text', role: 'caption', ...sub(box, 0, 0.89, 1, 0.11) }),
    ],
  }),

  // ---------- 2 photos ----------
  layout({
    id: 'pg-2-stacked',
    name: 'Two stacked',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['stack', 'pair', 'even'],
    slots: grid(box, 'page', { cols: 1, rows: 2 }),
  }),
  layout({
    id: 'pg-2-side',
    name: 'Two side by side',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['row', 'pair', 'even'],
    slots: grid(box, 'page', { cols: 2, rows: 1 }),
  }),
  layout({
    id: 'pg-2-hero-top',
    name: 'Big above, small below',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['hero', 'pair', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.66) }),
      slot({ id: 'b', ...sub(box, 0.2, 0.66 + G, 0.6, 0.34 - G) }),
    ],
  }),
  layout({
    id: 'pg-2-caption',
    name: 'Two with captions',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['stack', 'caption', 'journal'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.4) }),
      slot({ id: 'ca', kind: 'text', role: 'caption', ...sub(box, 0, 0.42, 1, 0.07) }),
      slot({ id: 'b', ...sub(box, 0, 0.53, 1, 0.4) }),
      slot({ id: 'cb', kind: 'text', role: 'caption', ...sub(box, 0, 0.95, 1, 0.05) }),
    ],
  }),

  // ---------- 3 photos ----------
  layout({
    id: 'pg-3-column',
    name: 'Three stacked',
    scope: 'page',
    families: TALL_FAMILIES,
    tags: ['stack', 'even', 'for-portrait'],
    slots: grid(box, 'page', { cols: 1, rows: 3 }),
  }),
  layout({
    id: 'pg-3-row',
    name: 'Three in a row',
    scope: 'page',
    families: WIDE_FAMILIES,
    tags: ['row', 'even', 'for-landscape'],
    slots: grid(box, 'page', { cols: 3, rows: 1 }),
  }),
  layout({
    id: 'pg-3-hero-top',
    name: 'Hero above, two below',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.6) }),
      ...grid(sub(box, 0, 0.6 + G, 1, 0.4 - G), 'page', { cols: 2, rows: 1, prefix: 'b' }),
    ],
  }),
  layout({
    id: 'pg-3-hero-bottom',
    name: 'Two above, hero below',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      ...grid(sub(box, 0, 0, 1, 0.4 - G), 'page', { cols: 2, rows: 1, prefix: 'a' }),
      slot({ id: 'b', ...sub(box, 0, 0.4, 1, 0.6) }),
    ],
  }),

  // ---------- 4 photos ----------
  layout({
    id: 'pg-4-grid',
    name: 'Grid of four',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['grid', 'even'],
    slots: grid(box, 'page', { cols: 2, rows: 2 }),
  }),
  layout({
    id: 'pg-4-strip',
    name: 'Four stacked bands',
    scope: 'page',
    families: TALL_FAMILIES,
    tags: ['stack', 'even', 'for-portrait'],
    slots: grid(box, 'page', { cols: 1, rows: 4 }),
  }),
  layout({
    id: 'pg-4-hero-top',
    name: 'Hero above, three below',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['hero', 'grid', 'asymmetric'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.62) }),
      ...grid(sub(box, 0, 0.62 + G, 1, 0.38 - G), 'page', { cols: 3, rows: 1, prefix: 'b' }),
    ],
  }),
  layout({
    id: 'pg-4-mosaic',
    name: 'Mosaic of four',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'asymmetric', 'editorial'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 0.6 - G / 2, 0.55) }),
      slot({ id: 'b', ...sub(box, 0.6, 0, 0.4, 0.55) }),
      slot({ id: 'c', ...sub(box, 0, 0.55 + G, 0.4 - G / 2, 0.45 - G) }),
      slot({ id: 'd', ...sub(box, 0.4, 0.55 + G, 0.6, 0.45 - G) }),
    ],
  }),
  layout({
    id: 'pg-4-title',
    name: 'Title and four',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['title', 'grid', 'chapter'],
    slots: (() => {
      const [strip, rest] = band(box, 0.14, 'top');
      return [slot({ id: 't', kind: 'text', role: 'title', ...strip }), ...grid(rest, 'page', { cols: 2, rows: 2 })];
    })(),
  }),

  // ---------- 5–8 photos ----------
  layout({
    id: 'pg-5-mosaic',
    name: 'Mosaic of five',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'dense', 'editorial'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.44) }),
      ...grid(sub(box, 0, 0.44 + G, 1, 0.28 - G), 'page', { cols: 2, rows: 1, prefix: 'b' }),
      ...grid(sub(box, 0, 0.72 + G, 1, 0.28 - G), 'page', { cols: 2, rows: 1, prefix: 'c' }),
    ],
  }),
  layout({
    id: 'pg-6-grid',
    name: 'Grid of six',
    scope: 'page',
    families: TALL_FAMILIES,
    tags: ['grid', 'even', 'contact-sheet'],
    slots: grid(box, 'page', { cols: 2, rows: 3 }),
  }),
  layout({
    id: 'pg-6-row-grid',
    name: 'Six across',
    scope: 'page',
    families: WIDE_FAMILIES,
    tags: ['grid', 'even', 'for-landscape'],
    slots: grid(box, 'page', { cols: 3, rows: 2 }),
  }),
  layout({
    id: 'pg-8-grid',
    name: 'Grid of eight',
    scope: 'page',
    families: TALL_FAMILIES,
    tags: ['grid', 'dense', 'contact-sheet'],
    slots: grid(box, 'page', { cols: 2, rows: 4 }),
  }),
  layout({
    id: 'pg-7-mosaic',
    name: 'Mosaic of seven',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['mosaic', 'dense'],
    slots: [
      slot({ id: 'a', ...sub(box, 0, 0, 1, 0.4) }),
      ...grid(sub(box, 0, 0.4 + G, 1, 0.3 - G), 'page', { cols: 3, rows: 1, prefix: 'b' }),
      ...grid(sub(box, 0, 0.7 + G, 1, 0.3 - G), 'page', { cols: 3, rows: 1, prefix: 'c' }),
    ],
  }),

  // ---------- text only ----------
  layout({
    id: 'pg-0-title',
    name: 'Title page',
    scope: 'page',
    families: ALL_FAMILIES,
    tags: ['text-only', 'title', 'chapter', 'opening'],
    slots: [
      slot({ id: 't', kind: 'text', role: 'title', ...sub(box, 0.05, 0.34, 0.9, 0.16) }),
      slot({ id: 'b', kind: 'text', role: 'body', ...sub(box, 0.05, 0.54, 0.9, 0.2) }),
    ],
  }),
];
